<script setup>
import { useRouter } from 'vue-router';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';

defineProps({
  isFetching: {
    type: Boolean,
    default: false,
  },
  isEmpty: {
    type: Boolean,
    default: false,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  totalCount: {
    type: Number,
    default: 100,
  },
  itemsPerPage: {
    type: Number,
    default: 25,
  },
  showPaginationFooter: {
    type: Boolean,
    default: false,
  },
  breadcrumbItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:currentPage']);

const router = useRouter();

const handlePageChange = event => {
  emit('update:currentPage', event);
};

const handleBreadcrumbClick = item => {
  router.push({
    name: item.routeName,
  });
};
</script>

<template>
  <section class="px-6 flex flex-col w-full h-screen overflow-y-auto bg-white">
    <div class="max-w-[60rem] mx-auto flex flex-col w-full h-full mb-4">
      <header class="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div class="relative overflow-hidden">
          <div class="absolute inset-0 pointer-events-none">
            <div
              class="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-70 bg-indigo-100"
            />
            <div
              class="absolute inset-0 opacity-60"
              style="
                background-image: radial-gradient(
                  rgba(15, 23, 42, 0.06) 1px,
                  transparent 1px
                );
                background-size: 22px 22px;
              "
            />
          </div>

          <div class="relative py-4">
            <Breadcrumb
              :items="breadcrumbItems"
              @click="handleBreadcrumbClick"
            />
          </div>
        </div>
      </header>

      <main class="flex gap-10 w-full flex-1 pb-16 pt-6">
        <section
          v-if="$slots.body || $slots.emptyState || isFetching"
          class="flex flex-col w-full"
        >
          <div
            v-if="isFetching"
            class="flex items-center justify-center py-10 text-slate-600"
          >
            <Spinner />
          </div>
          <div v-else-if="isEmpty">
            <slot name="emptyState" />
          </div>
          <slot v-else name="body" />
        </section>

        <section v-if="$slots.controls" class="flex w-full">
          <div class="w-full rounded-2xl border border-slate-200 bg-white p-5">
            <slot name="controls" />
          </div>
        </section>
      </main>

      <footer
        v-if="showPaginationFooter"
        class="sticky bottom-0 z-10 pb-4 bg-white"
      >
        <PaginationFooter
          :current-page="currentPage"
          :total-items="totalCount"
          :items-per-page="itemsPerPage"
          @update:current-page="handlePageChange"
        />
      </footer>
    </div>
  </section>
</template>
